// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { styledScrollbar } from "@rc/App/SharedStyles/sharedStyles";
import { buttonTransparent } from "@rc/XDL/PreStyle/button";
import { xBaseTheme } from "@rc/XDL/types";
import { color, ellipsis, xScreen } from "@rc/XDL/Utilities";
import { css } from "styled-components";

const MODAL_DESKTOP_PADDING = 20;
const MODAL_MOBILE_PADDING = 20;

const LARGE_MODAL_DESKTOP_PADDING = 30;

export default {
    sizeVariants: {
        small: 420,
        medium: 908,
        large: 999999999,
    },
    defaultProps: {
        cssOverrides: {
            modalBody: css`
                overflow: hidden;
                background: ${color("common.white")};
                overflow: unset;
                border: 1px solid ${color("common.border")};

                //Delete this media query for a full-screen modal on mobile
                ${xScreen("lte", "md")} {
                    max-width: 90%;
                    height: auto;
                    max-height: calc(100% - 35px);
                    margin: 15px;
                }
            `,
            headlineTypography: css`
                display: inline-block;
                width: 100%;
                letter-spacing: 0;
                line-height: 28px;
                margin-bottom: 0;
                word-wrap: break-word;
                white-space: pre-wrap;
                padding-right: 28px;
            `,
            titleButton: css`
                &&&& {
                    ${buttonTransparent}
                    width: auto;
                    height: 40px;
                    position: absolute;
                    top: 6px;
                    right: 4px;
                    padding: 12px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:focus {
                        outline: none;
                    }
                    & > ${TypographyStyle} {
                        font-size: 16px !important;
                        font-weight: 500;
                        letter-spacing: 0;
                        line-height: 24px;
                        color: ${color("common.ui5")};
                        padding: 0;
                        padding-left: 5px;
                        display: none;
                    }
                    & > ${IconWrapper} {
                        position: unset;
                        top: 0;
                        color: ${color("common.ui5")};
                    }
                    &:hover:not(:disabled) {
                        background-color: ${color("common.white")};
                        & > ${IconWrapper} {
                            color: ${color("primary")};
                        }
                    }
                }
            `,
            modalTitle: css`
                &&& {
                    border: 0;
                    color: ${color("common.ui6")};
                    font-size: 20px;
                    font-weight: 500;
                    letter-spacing: 0;
                    line-height: 32px;
                    padding: ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING / 2}px;
                    ${TypographyStyle} {
                        border-bottom: 1px solid ${color("common.border")};
                        padding-bottom: 6px;
                    }
                    ${xScreen("lte", "xs")} {
                        padding-left: ${MODAL_MOBILE_PADDING}px;
                        padding-right: ${MODAL_MOBILE_PADDING}px;
                    }
                    & > h4 {
                        // fix modal style for CMS
                        border: 0 !important;
                        padding-right: 0;
                        ${ellipsis(1)}
                    }

                    &.modal_title_large {
                        padding: 77px 35px 10px 35px;
                        button {
                            top: 25px;
                            right: 20px;
                            ${IconWrapper} svg {
                                color: ${color("primary")};
                            }
                        }
                        ${TypographyStyle} {
                            border-bottom: 0;
                            font-size: 30px;
                            line-height: 40px;
                        }
                        button ${TypographyStyle} {
                            display: inline-block;
                        }
                    }
                }
            `,
            modalContent: css`
                &&& {
                    padding: ${MODAL_DESKTOP_PADDING / 2}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px;
                    &.modal_content_large {
                        padding: 0 35px 15px 35px;
                    }
                }

                flex: 1;
                display: flex;
                flex-direction: column;

                ${styledScrollbar()}

                .modal-actions {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    width: 100%;
                    margin-top: 30px;

                    background: ${color("common.white")};

                    button {
                        margin-right: 15px;
                    }

                    button:last-child {
                        margin-right: 0;
                    }
                }

                ${xScreen("lte", "xs")} {
                    padding-left: ${MODAL_MOBILE_PADDING}px;
                    padding-right: ${MODAL_MOBILE_PADDING}px;

                    .modal-actions {
                        flex-direction: column;
                        justify-content: center;

                        button {
                            margin: 10px 0 0 0;
                            width: 100%;

                            &:first-child {
                                margin-top: 0;
                            }
                        }
                    }
                }
            `,
        },
        sizeVariant: "small",
        headlineTypographyProps: {
            variant: "h4",
            forwardAs: "h3",
            size: 20,
            weight: 500,
        },
        closeButtonProps: {
            buttonType: "solid",
            sizeVariant: "small",
            shape: "pill",
            variant: "primary",
            color: "common.ui5",
            size: 21,
        },
        closeButtonIconProps: {
            size: 20,
            color: "common.ui5",
        },
    },
} as xBaseTheme["modal"];
